import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, computed, input, output, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { Delivery, Insight, DeliverableService} from '..';
import { AvailableLanguage, FieldRequirements, Language, LanguageService, TranslatableFormControl, WordAndCharacterRequirements } from '../../language';
import { FormGroup, ValidatorFn } from '@angular/forms';
import { TranslationService } from '../../translation/translation.service';
import { FormDataStandard } from '../../utilities';
import { TranslocoService } from '@jsverse/transloco';
import { FormControlBooleanRequirements } from '../form-control-requirements.model';

interface GenericFormControl {
  key:string;
  defaultRequirements : any;
  deliverableAttribute:string;
}
@Component({
  selector: 'multisite-insight-edit',
  templateUrl: './insight-edit.component.html',
  styleUrls: ['./insight-edit.component.scss'],

  standalone: false,
})
export class InsightEditComponent implements OnInit, OnDestroy, OnChanges {

  // NECESSARY FOR ALL DELIVERABLE COMPONENTS

  delivery = input.required<Delivery>();
  deliveryLocked = input<boolean>(false); // for example, after submission
  formUpdateValueAndValidity = input<number>(0); // update the form value and validity when this changes
  formCachingServiceIdentifier = input.required<string | undefined>(); // required so we can immediately get cached data on instantiation
  disabled = input<boolean>(false);
  parentLoadingObject = input<{[key:string]:boolean}>({});
  loadingObject = signal <{[key:string]:boolean}>({});
  combinedLoadingObject = computed(() => ({...this.parentLoadingObject(),...this.loadingObject()}));
  saveDeliverable = output<{[key:string]:any}>();
  submitDeliverable = output<{formData:{[key:string]:any},allTargetLanguageFormControlsAreNull:boolean}>();
  deliverableForm : FormGroup;
  deliverable = input<Insight | undefined>(undefined); // Should this be required?
  
  // OPTIONAL FOR DELIVERABLE COMPONENTS (NEEDED BY SOME)
  
  truncatedTitle = input<string | undefined>(); // We will re-use this if the user saves the deliverable with an empty title

  // -- For components with text inputs: --

  deliverable_translations = input<{[key:string]:string} | undefined>(undefined);

  allNonTranslatableFormControls = signal<GenericFormControl[]>([]); // Not use in this component, but included in the logic for possible forked components
  allFormControls = computed(() => (this.allNonTranslatableFormControls() as (GenericFormControl | TranslatableFormControl)[]).concat(this.allTranslatableFormControls() as (GenericFormControl | TranslatableFormControl)[]));

  targetControls = computed(() => this.allTranslatableFormControls().filter(control => control.type === 'target'));
  sourceControls = computed(() => this.allTranslatableFormControls().filter(control => control.type === 'source'));

  sourceLanguage = input<Language | undefined>(undefined);
  targetLanguage = input<Language | undefined>(undefined);

  // These signal names are composed in the template by getSignalValue(). You will not find them by string-searches.
  sourceLanguageIsCharacterBased = computed(() => this.languageService.characterBasedLanguages?.includes(this.sourceLanguage()?.iso));
  targetLanguageIsCharacterBased = computed(() => this.languageService.characterBasedLanguages?.includes(this.targetLanguage()?.iso));

  wordAndCharacterRequirements = signal<{words?:WordAndCharacterRequirements[],characters?:WordAndCharacterRequirements[]}>({});
  taskBasedFormControlRequirements = signal<{required?:FormControlBooleanRequirements}>({});

  translatableFieldRequirements = computed(() => {
    const requirementsObject = {};
    this.allTranslatableFormControls().forEach(control => {
      requirementsObject[control.key] = this.getFieldRequirements(control.type === 'target' ? this.targetLanguage()?.iso : this.sourceLanguage()?.iso, control.pairKey, true);
    });
    return requirementsObject;
  });

  editorWords : {[key:string]:number} = {};
  editorCharacters : {[key:string]:number} = {};

  // -- For components which can have local errors: --

  error = signal<string|{message:string,meta:any}|undefined>(undefined);
  errorString = computed(() => {
    if(this.error()){
      return typeof this.error() === 'string' ? this.error() : (this.error() as {message:string,meta:any}).message;
    }
  });
  errorObject = computed(() => {
    const error = this.error();
    if (error && typeof error === 'object') {
      return error as { message: string; meta: any };
    }
    return null;
  });
  errorMessage = computed(() => {
    const errorObj = this.errorObject();
    if (errorObj && typeof errorObj === 'object' && 'message' in errorObj && errorObj.message) {
      return errorObj.message;
    }
  });

  // END OF OPTIONAL FOR DELIVERABLE COMPONENTS


  // SPECIFIC TO THIS DELIVERABLE COMPONENT

  /**
   * FALLBACKS ONLY — these are not the live limits, and reading them as though
   * they were is a mistake that has already been made once.
   *
   * The real bands come from the delivery's task (`delivery().task.meta`) and are
   * declared **per language**, because a language that says more per character
   * needs fewer of them. The live insight task carries `mainText` characters at
   * 200-900 by default, 100-400 for `ja`/`ko` and 50-200 for `zh`. These objects
   * are consulted only where a task supplies no record for the field at all.
   *
   * `LanguageService.characterBasedLanguages` (`ja`, `zh`, `ko`) is a separate
   * thing again: it decides whether the counter and progress bar show characters
   * or words. Validation is always on characters, in every language.
   */
  defaultHeadingRequirements = {
      words: {min: 1, max: 30},
      characters: {min: 2, max: 150},
      required: true,
  };
  defaultMainTextRequirements = {
    words: {min: 50, max: 120},
    characters: {min: 250, max: 600},
    required: true,
  }
  allTranslatableFormControls = signal<TranslatableFormControl[]>([
    {key:'heading_translation_source', type:'source', pairKey:'heading', defaultRequirements : this.defaultHeadingRequirements, deliverableAttribute:'s', deliverableTitle:false},
    {key:'heading', type:'target', pairKey:'heading', defaultRequirements : this.defaultHeadingRequirements, deliverableAttribute:'s', deliverableTitle:true},
    {key:'mainText_translation_source', type:'source', pairKey:'mainText', defaultRequirements : this.defaultMainTextRequirements, deliverableAttribute:'m', deliverableTitle:false},
    {key:'mainText', type:'target', pairKey:'mainText', defaultRequirements : this.defaultMainTextRequirements, deliverableAttribute:'m', deliverableTitle:false},
  ]);

  subscriptions : Subscription[] = [];


  // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT

  // NOT NECESSARY FOR THIS COMPONENT:

  activeLanguageObject = signal<AvailableLanguage | undefined>(undefined);

  // END OF NOT NECESSARY FOR THIS COMPONENT

  constructor(
    private languageService : LanguageService,
    private translationService : TranslationService,
    private translocoService : TranslocoService,
    private deliverableService : DeliverableService,
  ) {
  }

  isAnythingLoading(){
    for (let something in this.combinedLoadingObject()){
      if (this.combinedLoadingObject()[something]){
        return true;
      }
    }
    return false;
  }
  setLoadingStatus(key: string, value: boolean){
    // So we can detect deep changes in the object
    this.loadingObject.set({...this.loadingObject(),[key]:value});
  }

  getTranslatableFormFieldControls(pairKey : string){
    return this.allTranslatableFormControls() ? this.allTranslatableFormControls().filter(control => control.pairKey === pairKey) : [];
  };
  getFormFieldControls(key : string){
    return this.allNonTranslatableFormControls() ? this.allNonTranslatableFormControls().filter(control => control.key === key) : [];
  };

  // FOR COMPONENTS WHICH HAVE TRANSLATABLE TEXT INPUTS

  sendForTranslation(){
    this.error.set(undefined);
    if(!this.sourceLanguage()){
      return;
    }
    const textsAsObject = this.deliverableService.getFormSourceLanguageControlsAsTextObjectForTranslation(this.sourceControls(),this.deliverableForm);

    const defaultLocale : string = this.translationService.getDefaultLocaleFromGenericLanguage(this.targetLanguage().iso);
    if(this.allTargetLanguageFormControlsAreNull()){
      this.getTranslation(textsAsObject,defaultLocale,this.sourceLanguage().iso);
    } else {
      const translation = this.translocoService.translate('contributions.confirm.translation_overwrite');
      if (confirm(translation)) {
        this.getTranslation(textsAsObject,defaultLocale,this.sourceLanguage().iso);
      }
    }
  }
  getTranslation(textsAsObject:{[key:string]:string},target_lang : string, source_lang : string) {
    const keys = Object.keys(textsAsObject);
    const values = Object.values(textsAsObject);
    this.error.set(undefined);
    this.setLoadingStatus('translate',true);
    const translationSubscription = this.translationService.getTranslation(textsAsObject,target_lang,source_lang).subscribe((translations : {[key:string]:string}) => {
      this.setLoadingStatus('translate',false);
      this.deliverableService.handleFormTranslations(translations,this.deliverableForm, this.allTranslatableFormControls(), this.deliveryLocked());
    }, error => {
      this.setLoadingStatus('translate',false);
      this.error.set(error);
    });
    this.subscriptions.push(translationSubscription);
  }


  // END OF FOR COMPONENTS WHICH HAVE TRANSLATABLE TEXT INPUTS

  getFieldRequirements(language_code : string, field_key : string, translatable : boolean) : FieldRequirements{

    return this.deliverableService.getFieldRequirements(
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      language_code,
      field_key,
      translatable
    );
  };
  getSignalValue(signalName: string) {
    return this[signalName]();
  }
  getMax100 (progress : number){
    return Math.min(progress,100);
  }
  formControlCharacterCount(form : FormGroup, controlName: string): number {
    const control = form.get(controlName);
    return control?.value ? control.value.length : 0;
  }
  /* NOT NEEDED IN THIS SPECIFIC COMPONENT, but maybe needed if you use this as a template for another component
  optionalTranslatableControlHasMissingTranslation(controlDefinition: TranslatableFormControl): boolean | null { // This function is only needed for components with a translatable field which is also not a required field
    // Let's warn the user when a non-required field is written but the translation is missing
    // The source language is in the form but the target language is empty
    // If the delivery expects a target language, we will not save the untranslated source (target language is empty) when the delivery is submitted.
    // If the delivery does not expect a specify a language, we will save the untranslated source (target language is empty) when the delivery is submitted.
    return this.deliverableService.optionalTranslatableControlHasMissingTranslation(
      this.deliverableForm,
      this.allTranslatableFormControls(),
      controlDefinition
    );
  }
    */
  getSavedAndCachedFormData(): FormDataStandard {
    return this.deliverableService.getSavedAndCachedFormData(
      this.formCachingServiceIdentifier(),
      this.allNonTranslatableFormControls(),
      this.allTranslatableFormControls(),
      this.deliverable(),
      this.sourceLanguage(),
      this.deliverable_translations()
    );
  }

  cacheFormData(): void {
    this.deliverableService.cacheFormData(
      this.formCachingServiceIdentifier(),
      this.deliverableForm,
      this.allNonTranslatableFormControls(),
      this.allTranslatableFormControls(),
      this.sourceLanguage()
    );
  }
  
  checkAllCachedTargetLanguageFieldsAreNull(cachedFormData : FormDataStandard) : boolean {
    return this.targetControls().every(control => !cachedFormData?.[control.key]);
  };
  allTargetLanguageFormControlsAreNull() {
    if(!this.deliverableForm){return true;} 
    return this.targetControls().every(control => !this.deliverableForm.get(control.key)?.value);
  };

  // deliverableTargetLanguageTitleFormControlsIsNull is for debugging; you can comment it out

  // deliverableTargetLanguageTitleFormControlsIsNull() : boolean {
  //   if(!this.deliverableForm){return true;}
  //   const control = this.targetControls().find(control => control.deliverableTitle);
  //   let value = control ? this.deliverableForm.get(control.key)?.value : null;
  // value = typeof value === 'string' ? value.trim() : value;
  //   return !!(value ?? false);
  // };

  // end of debugging

  noSourceLanguageFormControlsAreNull() : boolean {
    return this.deliverableService.noFormControlsAreNull(this.deliverableForm,this.sourceControls());
  };
  disableAllTargetLanguageTranslatableFields(form : FormGroup){
    this.deliverableService.disableFormControls(this.deliverableForm,this.targetControls());
  };
  addOrRemoveEditingLanguage(language: Language): void {
    this.deliverableService.addOrRemoveEditingLanguage(
      this.deliverableForm,
      this.formCachingServiceIdentifier(),
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.targetControls(),
      language,
      this.deliverable(),
      this.deliverable_translations(),
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      this.deliveryLocked(),
      this.targetLanguage(),
    );
  }

  generateControlValidators(fieldRequirements: FieldRequirements): ValidatorFn[] {
    return this.deliverableService.generateControlValidators(fieldRequirements);
  }
  
  disableTargetLanguageTranslatableFieldsIfNecessary(sourceLanguage : Language){ // Applies the rule that the source language should be done first // TODO - move this function from all components to the DeliverableService (it's mostly duplicated there)
    const cachedFormData = this.getSavedAndCachedFormData();
    if(sourceLanguage && this.checkAllCachedTargetLanguageFieldsAreNull(cachedFormData)){
      this.disableAllTargetLanguageTranslatableFields(this.deliverableForm);
    }
  };
  showCharacters(characterCount : number, editorKey : string){
    this.editorCharacters[editorKey] = characterCount;
  }
  showWords(wordCount : number, editorKey : string){
    this.editorWords[editorKey] = wordCount;
  }

  save(action : 'update' | 'submit'){
    const formData = this.deliverableService.prepareFormDataForSaving(
      this.deliverableForm,
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.sourceLanguage(),
      this.truncatedTitle(),
      action,
      this.delivery().target_lang
    );
    if(action === 'update'){
      this.saveDeliverable.emit(formData);
    } else if (action === 'submit'){
      this.submitDeliverable.emit({formData:formData,allTargetLanguageFormControlsAreNull:this.allTargetLanguageFormControlsAreNull()});
    }
  }

  initialiseDeliverableForm(){

    const cachedFormData : FormDataStandard = this.getSavedAndCachedFormData();
    const translationTargetFormControls = this.allTranslatableFormControls().filter(control => control.type === 'target');

    const formGroupObject = this.deliverableService.generateInitialFormGroupObject(
      cachedFormData,
      this.allNonTranslatableFormControls(),
      translationTargetFormControls,
      this.targetLanguage(),
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      this.deliverable(),
    );
    this.deliverableForm = new FormGroup(formGroupObject);
    const formSubscription = this.deliverableForm.valueChanges.subscribe(() => {
      this.cacheFormData();
    });
    for (const control of translationTargetFormControls) {
      if(cachedFormData?.[control.key]){
        this.deliverableForm.get(control.key).markAsDirty();
      }
    }
    if(this.deliveryLocked()){
      this.deliverableForm.disable();
    }
    this.subscriptions.push(formSubscription);
  }

  initialise(){

    // -- For components with translatable text inputs --

    this.wordAndCharacterRequirements.set(this.delivery().task?.meta);
    this.taskBasedFormControlRequirements.set(this.delivery().task?.meta);

    // -- For all deliverable components --

    this.initialiseDeliverableForm();

    // -- For components with translatable text inputs --

    this.addOrRemoveEditingLanguage(this.sourceLanguage());
  }
  ngOnInit(): void {

    // SPECIFIC TO THIS DELIVERABLE COMPONENT

    // this.activeLanguageObject.set(this.languageService.activeLanguageObjectSynchronously);
    // const activeLanguageSubscription = this.languageService.activeLanguageObject.subscribe( (newActiveLanguage) => {
    //     // TODO - find a better way to prevent this being called when the component initialises. It should be called only when the language changes
    //     if (newActiveLanguage?.languageKey !== this.activeLanguageObject().languageKey){
    //       this.activeLanguageObject.set(newActiveLanguage);
    //       this.initialise();
    //     }
    // });
    // this.subscriptions.push(activeLanguageSubscription);

    // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT

    this.initialise();

  }
  ngOnDestroy () : void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Apply the form's enabled state from its two independent sources.
   *
   * They are not the same kind of thing, which is why this exists:
   *
   * - `deliveryLocked` is **permanent** — the delivery has been submitted
   *   (`!!delivery.submitted_at`) and must never be editable again.
   * - `disabled` is **transient** — the parent binds it to `isAnythingLoading()`,
   *   so it goes true and back to false around every load and every save.
   *
   * Either one disables the form; only when both are clear may it be enabled.
   *
   * This previously read the transient flag alone and called `enable()`
   * unconditionally when it cleared, so a submitted delivery locked itself on
   * init and then re-opened for editing the moment its first load finished.
   */
  private applyFormEnabledState(): void {
    if(!this.deliverableForm){
      return;
    }

    if(this.disabled() || this.deliveryLocked()){
      this.deliverableForm.disable();

      return;
    }

    this.deliverableForm.enable();

    if(this.sourceLanguage()){
      this.disableTargetLanguageTranslatableFieldsIfNecessary(this.sourceLanguage());
    }
  }

  ngOnChanges (changes: SimpleChanges) {

    if(changes.formUpdateValueAndValidity && this.deliverableForm){
      this.deliverableForm.updateValueAndValidity();
    }
    if((changes.disabled || changes.deliveryLocked) && this.deliverableForm){
      this.applyFormEnabledState();
    }
    if(changes.sourceLanguage && this.deliverableForm){
      if(changes.sourceLanguage){
        this.addOrRemoveEditingLanguage(changes.sourceLanguage.currentValue);
      }
    }
    
  }

}
